import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientBrandsRequest, ClientBrandsResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, isCompetitorBrandKind, isNewMetric, newMetricLabel } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsFixed, formatAnalyticsDate, formatAnalyticsAxisDate, brandColor, MEDIA_COLORS, SENTIMENT_COLORS, sentimentScaleColor, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateTopic } from '../shared/taxonomy.util';

interface BrandRowView {
	brand: string;
	isOurBrand: boolean;
	mentions: string;
	sov: string;
	avgReach: string;
	avgImpact: string;
	netSentiment: string;
	netSentimentTone: Tone;
	color: string;
}

interface ChangeView {
	text: string;
	tone: Tone;
}

interface RateOfChangeRowView {
	brand: string;
	isOurBrand: boolean;
	mentionsChg: ChangeView;
	reachChg: ChangeView;
	impactChg: ChangeView;
}

interface TopMediaOutletRowView {
	outlet: string;
	type: string;
	topBrand: string;
	mentions: string;
	share: string;
}

interface ImpactfulStoryView {
	id: string;
	headline: string;
	brand: string;
	isOurBrand: boolean;
	media: string;
	dateLabel: string;
	reach: string;
	impact: string;
	impactTone: Tone;
}

interface CompetitiveTopicRowView {
	topic: string;
	focusShare: number;
	compShare: number;
}

// SETTE redesign (CLIPPING-598): media type gets MEDIA_COLORS; brand identity now goes through
// the shared brandColor() (Задача 8) instead of this screen's own palette.
const MEDIA_TYPE_LABEL_KEYS: Record<string, string> = {
	WEB: 'ANALYTICS_BRANDS.MEDIA_SHORT_WEB',
	TV: 'ANALYTICS_BRANDS.MEDIA_SHORT_TV',
	PRINTED: 'ANALYTICS_BRANDS.MEDIA_SHORT_PRINT',
	RADIO: 'ANALYTICS_BRANDS.MEDIA_SHORT_RADIO',
	SOCIAL: 'ANALYTICS_BRANDS.MEDIA_SHORT_SOCIAL',
};
const MEDIA_TYPE_COLORS: Record<string, string> = { WEB: MEDIA_COLORS.portal, TV: MEDIA_COLORS.tv, PRINTED: MEDIA_COLORS.print, RADIO: MEDIA_COLORS.radio, SOCIAL: '#7D8796' };

// Ported from analytics-design/src/components/BrandsDashboard.tsx, then rewired to call
// POST /v1/client/analytics/brands instead of using sample data. "Manage Brands" (widget 1's
// link and the header button both open it) was a visual-only stub - it's tag/keyword CRUD, not
// an analytics read, same reasoning as every other "Manage ..." modal on this branch - and is now
// hidden (2026-10-06, product decision 2026-10-05: not in v1). Button + modal markup kept behind
// @if (false), not deleted. The "Rate of Change" table drops its Net Sentiment column - the backend only tracks current-period
// positive/neutral/negative counts, not the previous period's, so a sentiment delta isn't
// computable yet (Mentions/Reach/Impact deltas are real and kept).
@Component({
	selector: 'app-brands',
	standalone: true,
	imports: [FormsModule, MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent],
	templateUrl: './brands.component.html',
	styleUrl: './brands.component.scss',
})
export class BrandsComponent implements OnChanges {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientBrandsResponse | null = null;

	showManageModal = false;
	selectedCompetitorTagId = '';
	private readonly fetchTrigger = new Subject<ClientBrandsRequest>();

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getBrandsAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
					if (!this.selectedCompetitorTagId || !this.competitorOptions.some((c) => c.tagId === this.selectedCompetitorTagId)) {
						this.selectedCompetitorTagId = this.competitorOptions[0]?.tagId || '';
					}
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	// Фаза 4: the Brands screen's whole content is a company-vs-competitor comparison, so this
	// takes priority over isEmptyPeriod below - a client with mentions but zero Competitor-kind
	// tags configured needs "set up competitors", not "no mentions". Read from the panel-level
	// brandOptions Input (stable, available before any fetch), not from `data` - a data-derived
	// list would wrongly read as "no competitors" on a period where they had zero mentions.
	get hasCompetitors(): boolean {
		return this.brandOptions.some((b) => isCompetitorBrandKind(b.brandKind));
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.brands?.reduce((s, b) => s + (b.mentions ?? 0), 0) ?? 0) === 0;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientBrandsRequest);
	}

	openManageModal(): void {
		this.showManageModal = true;
	}

	closeManageModal(): void {
		this.showManageModal = false;
	}

	addCompetitorBrand(): void {
		window.alert('Add brand form is ready in settings.');
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	mediaTypeLabel(code: string): string {
		const key = MEDIA_TYPE_LABEL_KEYS[code];
		return key ? this.translate.instant(key) : code;
	}

	private get focusBrandRow() {
		const company = this.data?.brands?.find((b) => b.brandKind === 'COMPANY');
		return company || this.data?.brands?.[0];
	}

	get monitoredBrandsCount(): number {
		return this.data?.brands?.length ?? 0;
	}

	get totalMentions(): number {
		return (this.data?.brands ?? []).reduce((s, b) => s + (b.mentions ?? 0), 0);
	}

	get mentionsFootnote(): string {
		const f = this.focusBrandRow;
		if (!f) return '';
		return this.translate.instant('ANALYTICS_BRANDS.MENTIONS_FOOTNOTE', { name: f.name, count: formatAnalyticsInt(f.mentions ?? 0, this.lang), percent: formatAnalyticsNumber(f.sharePercent ?? 0, this.lang, 1) });
	}

	get monitoredBrandsFootnote(): string {
		return (this.data?.brands ?? []).map((b) => b.name).join(', ');
	}

	get netSentimentValue(): string {
		const f = this.focusBrandRow;
		return f?.netSentimentPercent != null ? `${formatSigned(f.netSentimentPercent, 1, this.lang)}%` : '—';
	}

	get netSentimentFootnote(): string {
		const f = this.focusBrandRow;
		if (!f) return '';
		return this.translate.instant('ANALYTICS_BRANDS.NET_SENTIMENT_FOOTNOTE', { name: f.name, pos: f.positiveCount, neg: f.negativeCount });
	}

	get sovValue(): string {
		const f = this.focusBrandRow;
		return f?.sharePercent != null ? `${formatAnalyticsNumber(f.sharePercent, this.lang, 1)}%` : '—';
	}

	get sovFootnote(): string {
		const sorted = [...(this.data?.brands ?? [])].sort((a, b) => (b.sharePercent ?? 0) - (a.sharePercent ?? 0));
		const f = this.focusBrandRow;
		const second = sorted.find((b) => b.tagId !== f?.tagId);
		if (!f || !second) return '';
		return this.translate.instant('ANALYTICS_BRANDS.SOV_FOOTNOTE_LEADS', { gap: formatAnalyticsFixed((f.sharePercent ?? 0) - (second.sharePercent ?? 0), this.lang, 1) });
	}

	get avgReachValue(): string {
		const f = this.focusBrandRow;
		return f?.avgReach != null ? `${formatAnalyticsNumber(f.avgReach, this.lang, 1)}/100` : '—';
	}

	get avgImpactValue(): string {
		const f = this.focusBrandRow;
		return f?.avgImpact != null ? formatSigned(f.avgImpact, 1, this.lang) : '—';
	}

	// "Врз основа на N спомнувања" caption - the focus brand's own mention count, backing its
	// Net Sentiment/SOV/Avg Visibility/Avg Impact KPI cards and the per-brand average bar charts.
	get focusBrandMentions(): number | undefined {
		return this.focusBrandRow?.mentions;
	}

	// Per-bar tooltip note (reach/impact/net-sentiment bar charts): the exact mention count behind
	// that one brand's average.
	private brandSampleNote(dataIndex: number): string {
		const b = this.data?.brands?.[dataIndex];
		if (!b) return '';
		const count = formatAnalyticsInt(b.mentions ?? 0, this.lang);
		return this.translate.instant('ANALYTICS_BRANDS.SAMPLE_NOTE', { count });
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	// --- Widget 1: Brand Comparison table ---

	get brandTableData(): BrandRowView[] {
		return (this.data?.brands ?? []).map((b) => ({
			brand: b.name || '',
			isOurBrand: b.brandKind === 'COMPANY',
			mentions: formatAnalyticsInt(b.mentions ?? 0, this.lang),
			sov: `${formatAnalyticsNumber(b.sharePercent ?? 0, this.lang, 1)}%`,
			avgReach: b.avgReach != null ? `${formatAnalyticsNumber(b.avgReach, this.lang, 1)}/100` : '—',
			avgImpact: b.avgImpact != null ? formatSigned(b.avgImpact, 1, this.lang) : '—',
			netSentiment: b.netSentimentPercent != null ? `${formatSignedAuto(Math.round(b.netSentimentPercent), this.lang)}%` : '—',
			netSentimentTone: signTone(b.netSentimentPercent ?? 0),
			color: brandColor(b.tagId, this.brandOptions),
		}));
	}

	// --- Widget 2: Mentions trend by brand ---

	get mentionsTrendChartData() {
		const series = this.data?.mentionsTrendByBrand ?? [];
		const labels = (series[0]?.points ?? []).map((p) => (p.bucketStart ? formatAnalyticsAxisDate(new Date(p.bucketStart), this.lang) : ''));
		return {
			labels,
			datasets: series.map((s, i) => ({
				label: s.name || '',
				data: (s.points ?? []).map((p) => p.mentions ?? 0),
				borderColor: brandColor(s.tagId, this.brandOptions),
				backgroundColor: 'transparent',
				borderWidth: i === 0 ? 2.5 : 1.5,
				tension: 0.35,
				pointRadius: 2.5,
				pointBackgroundColor: brandColor(s.tagId, this.brandOptions),
				fill: false,
			})),
		};
	}

	readonly mentionsTrendChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: 'index' as const, intersect: false },
		plugins: {
			legend: { display: false },
			tooltip: { backgroundColor: AXIS.tooltipBg, titleColor: '#ffffff', bodyColor: '#ffffff', padding: 8, cornerRadius: 10, bodyFont: { size: 11 }, titleFont: { size: 11, weight: 'bold' as const } },
		},
		scales: {
			x: { grid: { display: false }, ticks: { font: { size: 10 }, color: AXIS.text } },
			y: { grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text } },
		},
	};

	get trendLegend(): { name: string; color: string }[] {
		return (this.data?.mentionsTrendByBrand ?? []).slice(0, 4).map((s) => ({ name: s.name || '', color: brandColor(s.tagId, this.brandOptions) }));
	}

	// --- Widget 3: SOV doughnut ---

	get sovLegend(): { name: string; color: string; sov: string }[] {
		return (this.data?.brands ?? []).map((b) => ({ name: b.name || '', color: brandColor(b.tagId, this.brandOptions), sov: `${formatAnalyticsNumber(b.sharePercent ?? 0, this.lang, 1)}%` }));
	}

	get sovDoughnutChartData() {
		const brands = this.data?.brands ?? [];
		return { labels: brands.map((b) => b.name), datasets: [{ data: brands.map((b) => b.sharePercent ?? 0), backgroundColor: brands.map((b) => brandColor(b.tagId, this.brandOptions)), borderWidth: 2, borderColor: '#ffffff', hoverOffset: 4 }] };
	}

	readonly sovDoughnutChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '72%',
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				bodyFont: { size: 11 },
				callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_BRANDS.SOV_TOOLTIP', { label: ctx.label, percent: formatAnalyticsNumber(ctx.parsed, this.lang, 1), count: formatAnalyticsInt(this.data?.brands?.[ctx.dataIndex]?.mentions ?? 0, this.lang) })}` },
			},
		},
	};

	// --- Widget 4/5/6: reach / impact / net sentiment bars ---

	get reachBarChartData() {
		const brands = this.data?.brands ?? [];
		return { labels: brands.map((b) => b.name), datasets: [{ data: brands.map((b) => b.avgReach ?? 0), backgroundColor: brands.map((b) => brandColor(b.tagId, this.brandOptions)), borderRadius: 4, barThickness: 15 }] };
	}

	readonly reachBarChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_BRANDS.AVG_VISIBILITY_TOOLTIP', { value: formatAnalyticsNumber(ctx.parsed.x, this.lang, 1), note: this.brandSampleNote(ctx.dataIndex) })}` } } },
		scales: {
			x: { min: 0, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text } },
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// SETTE redesign (CLIPPING-598), Правило А: Impact is signed, so it's the one bar chart
	// here allowed to color by sign - via signTone (0 is neutral, never positive-by-default).
	get impactBarChartData() {
		const brands = this.data?.brands ?? [];
		const values = brands.map((b) => b.avgImpact ?? 0);
		return { labels: brands.map((b) => b.name), datasets: [{ data: values, backgroundColor: (context: { raw: unknown }) => SENTIMENT_COLORS[signTone(Number(context.raw))], borderRadius: 4, barThickness: 15 }] };
	}

	readonly impactBarChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_BRANDS.AVG_IMPACT_TOOLTIP', { value: formatSignedAuto(ctx.parsed.x, this.lang), note: this.brandSampleNote(ctx.dataIndex) })}` } } },
		scales: {
			x: {
				min: -100,
				max: 100,
				grid: { color: (ctx: { tick: { value: number } }) => (ctx.tick.value === 0 ? AXIS.baseline : AXIS.grid), lineWidth: (ctx: { tick: { value: number } }) => (ctx.tick.value === 0 ? 1.5 : 1) },
				ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => formatSignedAuto(Number(v), this.lang) },
			},
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// SETTE redesign (CLIPPING-598), Фаза 6 fix: the 4-tier threshold (incl. a blue tier at
	// +20..+39, a Принцип 1 violation) is replaced with the shared symmetric 5-level
	// sentimentScaleColor(value, 40) - the exact scale used everywhere else sentiment is shown.
	get netSentimentChartData() {
		const brands = this.data?.brands ?? [];
		const values = brands.map((b) => b.netSentimentPercent ?? 0);
		return {
			labels: brands.map((b) => b.name),
			datasets: [{ label: this.translate.instant('ANALYTICS_BRANDS.NET_SENTIMENT_DATASET_LABEL'), data: values, backgroundColor: (context: { raw: unknown }) => sentimentScaleColor(Number(context.raw), 40), borderRadius: 4, barThickness: 15 }],
		};
	}

	readonly netSentimentChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_BRANDS.NET_SENTIMENT_TOOLTIP', { value: formatSignedAuto(ctx.parsed.x, this.lang), note: this.brandSampleNote(ctx.dataIndex) })}` } } },
		scales: {
			x: { min: -30, max: 60, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => `${v}%` } },
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 7: Mentions by media type (100% stacked) ---

	get mediaTypeKeys(): string[] {
		return (this.data?.mediaTypeByBrand?.columns ?? []).map((c) => c.key || '');
	}

	get mediaStackedChartData() {
		const matrix = this.data?.mediaTypeByBrand;
		const rows = matrix?.rows ?? [];
		const keys = this.mediaTypeKeys;
		return {
			labels: rows.map((r) => r.rowLabel || ''),
			datasets: keys.map((key) => ({
				label: this.mediaTypeLabel(key),
				data: rows.map((r) => {
					const total = r.totalMentions || 1;
					const cell = r.cellsByColumnKey?.[key];
					return total > 0 ? (((cell?.mentions ?? 0) / total) * 100) : 0;
				}),
				backgroundColor: MEDIA_TYPE_COLORS[key] || '#7D8796',
				barThickness: 15,
			})),
		};
	}

	readonly mediaStackedChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const key = this.mediaTypeKeys[ctx.datasetIndex];
						const row = this.data?.mediaTypeByBrand?.rows?.[ctx.dataIndex];
						const n = row?.cellsByColumnKey?.[key]?.mentions ?? 0;
						return ` ${this.translate.instant('ANALYTICS_BRANDS.MEDIA_STACKED_TOOLTIP', { label: ctx.dataset.label, percent: formatAnalyticsInt(ctx.parsed.x, this.lang), count: formatAnalyticsInt(n, this.lang) })}`;
					},
				},
			},
		},
		scales: {
			x: { stacked: true, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => `${v}%` } },
			y: { stacked: true, grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	get mediaTypeLegend(): { label: string; color: string }[] {
		return this.mediaTypeKeys.map((k) => ({ label: this.mediaTypeLabel(k), color: MEDIA_TYPE_COLORS[k] || '#7D8796' }));
	}

	// --- Widget 8: Top media per brand ---

	get topMediaOutlets(): TopMediaOutletRowView[] {
		return (this.data?.topMediaPerBrand ?? []).slice(0, 8).map((r) => ({
			outlet: r.name || '',
			type: this.mediaTypeLabel(r.type || ''),
			topBrand: r.topBrandName || '',
			mentions: formatAnalyticsInt(r.mentions ?? 0, this.lang),
			share: r.sharePercentOfThatBrand != null ? `${formatAnalyticsNumber(r.sharePercentOfThatBrand, this.lang, 1)}%` : '—',
		}));
	}

	// --- Widget 9: Most impactful mentions ---

	get impactfulStories(): ImpactfulStoryView[] {
		return (this.data?.mostImpactfulMentions ?? []).slice(0, 4).map((s) => ({
			id: s.clipId || '',
			headline: s.headline || '',
			brand: s.brandTagName || '',
			isOurBrand: this.brandOptions.find((b) => b.name === s.brandTagName)?.brandKind === 'COMPANY',
			media: s.outletName || '',
			dateLabel: s.publicationDate ? formatAnalyticsDate(new Date(s.publicationDate), this.lang) : '',
			reach: s.reach != null ? formatAnalyticsNumber(s.reach, this.lang, 1) : '—',
			impact: s.impact != null ? formatSignedAuto(s.impact, this.lang) : '—',
			impactTone: signTone(s.impact ?? 0),
		}));
	}

	// --- Widget 10: Rate of change ---

	get rateOfChangeData(): RateOfChangeRowView[] {
		return (this.data?.brands ?? [])
			.filter((b) => b.previousMentions != null)
			.map((b) => ({
				brand: b.name || '',
				isOurBrand: b.brandKind === 'COMPANY',
				mentionsChg: this.pctChange(b.mentions, b.previousMentions, b.hasEnoughCompareData),
				reachChg: this.ptsChange(b.avgReach, b.previousAvgReach, b.hasEnoughCompareData),
				impactChg: this.ptsChange(b.avgImpact, b.previousAvgImpact, b.hasEnoughCompareData),
			}));
	}

	// Same shared comparison rule as every other delta in the Analytics screens: no usable
	// previous value at all (the compare period had 0 mentions) -> "Ново" if this period has a
	// real value, else nothing. Both now return a {text, tone} pair instead of a pre-colored
	// string, since deltaTone's "more is good" sign (and U+2212 for negatives) can't be recovered
	// later by sniffing the formatted text.
	private pctChange(current?: number, previous?: number | null, hasEnoughCompareData?: boolean): ChangeView {
		const comparison = resolveComparison(isCompareRequested(this.filters), previous, hasEnoughCompareData);
		if (comparison.insufficient) {
			return current != null && isNewMetric(current, 0) ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '—', tone: 'neutral' };
		}
		if (current == null || !comparison.show) return { text: '—', tone: 'neutral' };
		const prev = comparison.previous as number;
		if (prev === 0) return current > 0 ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '0%', tone: 'neutral' };
		const pct = Math.round(((current - prev) / prev) * 100);
		return { text: `${formatSignedAuto(pct, this.lang)}%`, tone: signTone(pct) };
	}

	private ptsChange(current?: number | null, previous?: number | null, hasEnoughCompareData?: boolean): ChangeView {
		const comparison = resolveComparison(isCompareRequested(this.filters), previous, hasEnoughCompareData);
		if (comparison.insufficient) {
			return current != null && isNewMetric(current, 0) ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '—', tone: 'neutral' };
		}
		if (current == null || !comparison.show) return { text: '—', tone: 'neutral' };
		const delta = current - (comparison.previous as number);
		return { text: `${formatSigned(delta, 1, this.lang)} pts`, tone: signTone(delta) };
	}

	// --- Widget 11: Competitive topic positioning ---

	get competitorOptions(): { tagId: string; name: string; sov: string }[] {
		const seen = new Map<string, { tagId: string; name: string; sov: string }>();
		for (const t of this.data?.topicPositioning ?? []) {
			for (const c of t.competitorShares ?? []) {
				if (c.tagId && !seen.has(c.tagId)) {
					const brandRow = this.data?.brands?.find((b) => b.tagId === c.tagId);
					seen.set(c.tagId, { tagId: c.tagId, name: c.name || '', sov: brandRow?.sharePercent != null ? `${formatAnalyticsNumber(brandRow.sharePercent, this.lang, 1)}%` : '—' });
				}
			}
		}
		return Array.from(seen.values());
	}

	get selectedCompetitorColor(): string {
		return brandColor(this.selectedCompetitorTagId, this.brandOptions);
	}

	get selectedCompetitorName(): string {
		return this.competitorOptions.find((c) => c.tagId === this.selectedCompetitorTagId)?.name || '';
	}

	get sortedCompetitiveTopics(): CompetitiveTopicRowView[] {
		const rows: CompetitiveTopicRowView[] = (this.data?.topicPositioning ?? [])
			.map((t) => {
				const comp = (t.competitorShares ?? []).find((c) => c.tagId === this.selectedCompetitorTagId);
				return { topic: translateTopic(t.topic, this.translate), focusShare: Math.round(t.focusSharePercent ?? 0), compShare: Math.round(comp?.sharePercent ?? 0) };
			})
			.filter((r) => r.focusShare > 0 || r.compShare > 0);
		return rows.sort((a, b) => Math.abs(b.focusShare - b.compShare) - Math.abs(a.focusShare - a.compShare));
	}

	topicGap(row: CompetitiveTopicRowView): number {
		return row.focusShare - row.compShare;
	}

	topicIsEven(row: CompetitiveTopicRowView): boolean {
		return Math.abs(this.topicGap(row)) <= 2;
	}

	topicFocusLeads(row: CompetitiveTopicRowView): boolean {
		return this.topicGap(row) > 0;
	}

	topicRowClass(row: CompetitiveTopicRowView): string {
		const absGap = Math.abs(this.topicGap(row));
		if (this.topicFocusLeads(row) && absGap >= 15) return 'bg-blue-tint border-blue-tint';
		if (!this.topicFocusLeads(row) && absGap >= 15) return 'bg-surface-gray border-line';
		return 'bg-surface border-line hover:bg-surface-blue';
	}

	get dynamicFooterSummary(): string {
		const sorted = this.sortedCompetitiveTopics;
		const focusName = this.focusBrandRow?.name || this.translate.instant('ANALYTICS_GLOSSARY.OUR_BRAND');
		const focusLeads = sorted.filter((t) => t.focusShare > t.compShare).map((t) => ({ topic: t.topic, gap: t.focusShare - t.compShare }));
		const compLeads = sorted.filter((t) => t.compShare > t.focusShare).map((t) => ({ topic: t.topic, gap: t.compShare - t.focusShare }));
		if (focusLeads.length >= 1 && compLeads.length >= 1) {
			return this.translate.instant('ANALYTICS_BRANDS.FOOTER_BOTH_LEAD', {
				focusName,
				focusTopic: focusLeads[0].topic,
				focusGap: focusLeads[0].gap,
				compName: this.selectedCompetitorName,
				compTopic: compLeads[0].topic,
				compGap: compLeads[0].gap,
			});
		}
		if (focusLeads.length >= 1) {
			return this.translate.instant('ANALYTICS_BRANDS.FOOTER_FOCUS_LEADS', { focusName, topic: focusLeads[0].topic, gap: focusLeads[0].gap });
		}
		return this.translate.instant('ANALYTICS_BRANDS.FOOTER_HOLDS_POSITION', { focusName, compName: this.selectedCompetitorName });
	}
}
